import { User } from '../models/user';

/**
 * Organization-level permission utilities
 * These check permissions at the organization level (not case-specific)
 */

/**
 * Check if user is an organization admin
 */
export const isOrganizationAdmin = (user: User | null): boolean => {
    if (!user) return false;
    return user.organizationRole === 'admin' || user.organizationRole === 'owner';
};

/**
 * Check if user can manage organization settings
 */
export const canManageOrganization = (user: User | null): boolean => {
    return isOrganizationAdmin(user);
};

/**
 * Check if user can invite members
 */
export const canInviteMembers = (user: User | null): boolean => {
    return isOrganizationAdmin(user);
};

/**
 * Check if user can manage members (change roles, remove)
 */
export const canManageMembers = (user: User | null): boolean => {
    return isOrganizationAdmin(user);
};

/**
 * Check if user can switch organizations
 * (requires multi-org access - to be implemented with Stytch discovery)
 */
export const canSwitchOrganizations = (user: User | null): boolean => {
    // For now, all authenticated users can potentially switch
    // This will be enhanced when we implement multi-org discovery
    return !!user;
};

/**
 * Get user's organization role label
 */
export const getOrganizationRoleLabel = (role?: 'owner' | 'admin' | 'member'): string => {
    if (!role) return 'No Role';

    const labels: Record<'owner' | 'admin' | 'member', string> = {
        owner: 'Owner',
        admin: 'Administrator',
        member: 'Member'
    };

    return labels[role] || 'Unknown';
};

/**
 * Get organization role description
 */
export const getOrganizationRoleDescription = (role: 'owner' | 'admin' | 'member'): string => {
    const descriptions: Record<'owner' | 'admin' | 'member', string> = {
        owner: 'Full ownership and control over the organization',
        admin: 'Full control over organization settings and members',
        member: 'Access to organization resources based on case permissions'
    };

    return descriptions[role];
};
